import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setCurrentExhibit, setExhibitStatus, ExhibitId } from '../store/slices/exhibitSlice';
import { TIMEOUT_DURATION_MS } from '../utils/constants';

// Import exhibit-specific components
import OrangLaut from '../components/exhibits/OrangLaut';
import BookOfMyths from '../components/exhibits/BookOfMyths';
import SG60 from '../components/exhibits//SG60';
import MarketTraders from '../components/exhibits/MarketTraders';
import Badang from '../components/exhibits/Badang';
import PeranakanDesign from '../components/exhibits/PeranakanDesign';
import TimeCapsule from '../components/exhibits/TimeCapsule';

// Import shared exhibit components
import ExperienceBusy from '../components/exhibits/shared/ExperienceBusy';
import NoPersonalization from '../components/exhibits/shared/NoPersonalization';
import TimeoutScreen from '../components/exhibits/shared/TimeoutScreen';
import LoadingScreen from '../components/exhibits/shared/LoadingScreen';

export default function ExhibitPage() {
    const { exhibitId } = useParams<{ exhibitId: string }>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    
    const { currentExhibit, exhibitStatus, hasTimeout } = useAppSelector(state => state.exhibit);
    const { isAuthenticated, hasCompletedRegistration } = useAppSelector(state => state.user);
    
    // Simulate checking if exhibit is available
    useEffect(() => {
        if (!exhibitId) return;
        
        setLoading(true);
        
        // Convert the URL param to our exhibit ID type
        const validExhibitId = exhibitId as ExhibitId;
        
        // Set current exhibit in state
        dispatch(setCurrentExhibit(validExhibitId));
        
        // Simulate API call to check availability
        const checkAvailability = async () => {
            // In a real app, this would be an API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Randomly set availability for demo (would be from API in real app)
            const status = Math.random() > 0.8 ? 'busy' : 'available';
            dispatch(setExhibitStatus({ id: validExhibitId, status: status as 'busy' | 'available' }));
            setLoading(false);
        };
        
        checkAvailability();
        
        // Zone-based exhibits (orang-laut, market-traders) now handle their own timeouts
        // Global exhibit timeout disabled for zone-based exhibits
        const zoneBasedExhibits = ['orang-laut', 'market-traders'];
        if (!zoneBasedExhibits.includes(validExhibitId)) {
            // Set up a timeout using the configured duration for non-zone exhibits
            const timeoutId = setTimeout(() => {
                dispatch({ type: 'exhibit/setTimeout', payload: true });
            }, TIMEOUT_DURATION_MS);
            
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [exhibitId, dispatch]);
    
    // Handle when user hasn't completed registration for personalized experiences
    const requiresPersonalization = ['book-of-myths', 'time-capsule'].includes(exhibitId || '');
    if (requiresPersonalization && !hasCompletedRegistration) {
        return <NoPersonalization />;
    }
    
    // Handle exhibit busy state
    // if (!loading && exhibitStatus[exhibitId as ExhibitId] === 'busy') {
    //     return <ExperienceBusy />;
    // }
    
    // Handle timeout state
    // if (hasTimeout) {
    //     return <TimeoutScreen exhibitId={exhibitId as ExhibitId} />;
    // }
    
    // Show loading screen
    if (loading) {
        return <LoadingScreen />;
    }
    
    // Render the appropriate exhibit component
    switch (exhibitId) {
        case 'orang-laut':
            return <OrangLaut />;
        case 'book-of-myths':
            return <BookOfMyths />;
        case 'sg60':
            return <SG60 />;
        case 'market-traders':
            return <MarketTraders />;
        case 'badang':
            return <Badang />;
        case 'peranakan-design':
            return <PeranakanDesign />;
        case 'time-capsule':
            return <TimeCapsule />;
        default:
            // Redirect to home if invalid exhibit
            navigate('/');
            return null;
    }
} 